import { iEmail } from "../../interfaces/emailModel";

const KB = 1024;

// binary parts are base64 encoded when the mail is sent, which inflates them by 4/3.
// the limit applies to the encoded message, so the byte counts are scaled by this.
export const ENCODING_FACTOR = 4 / 3;

// size of the body in KB, discounting the inline images that are already counted through
// ed.inlineAttachments. right after a message is embedded the body still holds those images
// as data: URIs, and checkInlineImages() swaps them for short blob: URIs the next time the
// composer is entered - discounting them keeps the total the same in both states.
// a data: image with no matching inline attachment is left in the count, nothing else knows its size.
const bodySizeKB = (body: string, inlineContentIds: Array<string>): number => {
    const html = body ?? "";
    if (html === "" || inlineContentIds.length === 0) {
        return new Blob([html]).size / KB;
    }
    let DOMParsing = new DOMParser();
    let parsed = DOMParsing.parseFromString(html, "text/html");
    let images = parsed.querySelectorAll("img");
    for (var i = 0; i < images.length; i++) {
        if (images[i].getAttribute("src")?.startsWith("data:") && inlineContentIds.indexOf(images[i].title) > -1) {
            images[i].removeAttribute("src");
        }
    }
    return new Blob([parsed.documentElement.outerHTML]).size / KB;
};

// total size of a draft in KB: body + attachments + inline images + the attachments of an
// embedded message. inline images have to be counted from inlineAttachments and not from the
// body, because the body only holds a reference to them once the composer has been re-entered.
// every stored size is in bytes.
export const draftSizeKB = (ed: iEmail): number => {
    // an inline image only counts while the body still references it by mimeContentID. embedding a
    // message creates fresh inline parts and leaves the previous ones on the draft, so a remove and
    // re-embed would otherwise add the same images to the total again. prepareBody() uses the same
    // test to decide which inline parts to delete when the mail is sent.
    const body = ed.body ?? "";
    const inlineAttachments = (ed.inlineAttachments ?? []).filter(
        a => a.mimeContentID !== undefined && body.includes(a.mimeContentID),
    );
    const inlineContentIds = inlineAttachments
        .map(a => a.mimeContentID)
        .filter((id): id is string => id !== undefined);

    const attachmentsKB = ed.attachments.reduce((sum, a) => sum + (a.size || 0), 0) / KB;
    const inlineKB = inlineAttachments.reduce((sum, a) => sum + (a.size || 0), 0) / KB;
    const embeddedKB = (ed.embeddedMessageAttachmentsSize ?? 0) / KB;

    return bodySizeKB(body, inlineContentIds) + (attachmentsKB + inlineKB + embeddedKB) * ENCODING_FACTOR;
};
